import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Stack } from "@chakra-ui/react";
import { t } from "i18next";
import { ManageScopes } from "./ManageScopes";
import { ManagePermissions } from "./ManagePermissions";
import { ApiClient, ResourceType } from "../../../models/ApiClient";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import { toast } from "../../../../shared/views/CustomToaster";

const i18prefix = "account:Integrations";

const parseInitialPermissions = (permissionsStr: string) => {
  const permissions = new Map<string, ResourceType>();

  if (permissionsStr) {
    permissionsStr.split(",").forEach((line) => {
      const [resource, entity] = line.split("/");
      permissions.set(entity, resource as ResourceType);
    });
  }

  return permissions;
};

interface Props {
  apiClient: ApiClient;
  onClose: () => void;
}

const ManagePermissionsModalBase: React.FC<Props> = ({
  onClose,
  apiClient,
}) => {
  const { accountStore } = useStores();
  const [submitting, setSubmitting] = useState(false);
  const [scopes, setScopes] = useState(new Set(apiClient.scopes));
  const [permissions, setPermissions] = useState(() =>
    parseInitialPermissions(apiClient.permissions)
  );

  const onSubmit = () => {
    setSubmitting(true);
    accountStore
      .updateApiClient(apiClient.id, {
        name: apiClient.name,
        permissions: [...permissions]
          .map(([entity, resource]) => `${resource}/${entity}`)
          .join(","),
        scopes: [...scopes],
      })
      .then(() => {
        toast("success", t(`${i18prefix}.toast.saveSuccess`));
        onClose();
      })
      .catch(() => {
        toast("error", t(`${i18prefix}.toast.saveFailed`));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <CustomDialog
      onClose={onClose}
      size="lg"
      header={t(`${i18prefix}.permissionsModal.title`)}
      footer={
        <Button
          flexShrink={0}
          colorPalette="primary"
          onClick={onSubmit}
          loading={submitting}
        >
          {t(`${i18prefix}.permissionsModal.submit`)}
        </Button>
      }
    >
      <Stack gap={4} fontSize="sm">
        <ManageScopes scopes={scopes} setScopes={setScopes} />

        <ManagePermissions
          apiClient={apiClient}
          permissions={permissions}
          setPermissions={setPermissions}
        />
      </Stack>
    </CustomDialog>
  );
};

export const ManagePermissionsModal = observer(ManagePermissionsModalBase);
